import { useState, useEffect, useMemo } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { listWorklogsApi, listProjectsApi, listUsersApi, approveWorklogsApi } from '../api/http';
import FormRow from '../components/FormRow';

const pad = (n) => String(n).padStart(2, '0');

export default function AdminWorklogApproval() {
    const { token, user } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filterMonth, setFilterMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
    });
    const [filterProject, setFilterProject] = useState('');
    const [filterUser, setFilterUser] = useState('');
    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);

    const [selectedIds, setSelectedIds] = useState(new Set());

    // Cargar filtros
    useEffect(() => {
        listProjectsApi(token).then(setProjects).catch(() => { });
        listUsersApi(token).then(setUsers).catch(() => { });
    }, [token]);

    // Cargar logs
    const fetchLogs = async () => {
        setLoading(true);
        try {
            const q = {
                month: filterMonth,
                admin_approved: 0,
                limit: 500
            };
            if (filterProject) q.project_id = filterProject;
            if (filterUser) q.user_id = filterUser;

            const data = await listWorklogsApi(token, q);
            setLogs(data || []);
            setSelectedIds(new Set()); // Reset selection on refetch
        } catch (err) {
            toast.error('Error al cargar registros');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [filterMonth, filterProject, filterUser, token]);

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = new Set(logs.map(l => l.id));
            setSelectedIds(allIds);
        } else {
            setSelectedIds(new Set());
        }
    };

    const handleSelectOne = (id, checked) => {
        const next = new Set(selectedIds);
        if (checked) next.add(id);
        else next.delete(id);
        setSelectedIds(next);
    };

    const handleApprove = async () => {
        if (selectedIds.size === 0) return;
        if (!window.confirm(`¿Aprobar ${selectedIds.size} registros seleccionados?`)) return;

        try {
            await approveWorklogsApi(Array.from(selectedIds), 1, token);
            toast.success(`${selectedIds.size} registros aprobados`);
            fetchLogs(); // Recargar
        } catch (err) {
            toast.error('Error al aprobar registros');
            console.error(err);
        }
    };

    const totalHours = useMemo(() => logs.reduce((acc, l) => acc + Number(l.hours || 0), 0), [logs]);
    const totalAmount = useMemo(() => logs.reduce((acc, l) => acc + Number(l.amount || 0), 0), [logs]);

    return (
        <Layout title="Aprobación de Bitácora">
            <Toaster position="top-right" />

            <div className="card worklog-card-spacing">
                <div className="worklog-header-actions" style={{ marginBottom: 20 }}>
                    <h2 style={{ margin: 0 }}>Pendientes de Aprobación</h2>
                    <button
                        className="btn success"
                        onClick={handleApprove}
                        disabled={selectedIds.size === 0}
                    >
                        Aprobar Seleccionados ({selectedIds.size})
                    </button>
                </div>

                {/* Filtros */}
                <div className="worklog-form-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                    <FormRow label="Mes">
                        <input
                            type="month"
                            value={filterMonth}
                            onChange={e => setFilterMonth(e.target.value)}
                            className="worklog-select-full"
                        />
                    </FormRow>
                    <FormRow label="Proyecto">
                        <select
                            value={filterProject}
                            onChange={e => setFilterProject(e.target.value)}
                            className="worklog-select-full"
                        >
                            <option value="">(Todos)</option>
                            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </FormRow>
                    <FormRow label="Programador">
                        <select
                            value={filterUser}
                            onChange={e => setFilterUser(e.target.value)}
                            className="worklog-select-full"
                        >
                            <option value="">(Todos)</option>
                            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </FormRow>
                </div>

                {/* Resumen */}
                <div className="worklog-pills-container" style={{ marginBottom: 20 }}>
                    <div className="pill-base pill-info">Registros: <span className="pill-value">{logs.length}</span></div>
                    <div className="pill-base pill-plain">Horas Totales: <span className="pill-value">{totalHours.toFixed(2)}</span></div>
                    <div className="pill-base pill-success">Monto Total: <span className="pill-value">${totalAmount.toLocaleString('es-CL')}</span></div>
                </div>

                {/* Tabla */}
                <div className="table-scroll">
                    <table className="worklog-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                                <th style={{ padding: 12, width: 40 }}>
                                    <input
                                        type="checkbox"
                                        checked={logs.length > 0 && selectedIds.size === logs.length}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th style={{ padding: 12 }}>Fecha</th>
                                <th style={{ padding: 12 }}>Programador</th>
                                <th style={{ padding: 12 }}>Proyecto</th>
                                <th style={{ padding: 12 }}>Detalle</th>
                                <th style={{ padding: 12, textAlign: 'right' }}>Horas</th>
                                <th style={{ padding: 12, textAlign: 'right' }}>Monto</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center' }}>Cargando...</td></tr>
                            ) : logs.length === 0 ? (
                                <tr><td colSpan={7} style={{ padding: 20, textAlign: 'center' }}>No hay registros pendientes</td></tr>
                            ) : (
                                logs.map(log => (
                                    <tr key={log.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: 12 }}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.has(log.id)}
                                                onChange={e => handleSelectOne(log.id, e.target.checked)}
                                            />
                                        </td>
                                        <td style={{ padding: 12 }}>
                                            {new Date(log.started_at).toLocaleDateString()} <br />
                                            <small style={{ color: '#6b7280' }}>
                                                {new Date(log.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                                                {new Date(log.ended_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </small>
                                        </td>
                                        <td style={{ padding: 12 }}>{log.member_name}</td>
                                        <td style={{ padding: 12 }}>
                                            <b>{log.project}</b>
                                            {log.client && <div style={{ fontSize: '0.85em', color: '#6b7280' }}>{log.client}</div>}
                                        </td>
                                        <td style={{ padding: 12, maxWidth: 300 }}>
                                            {log.activity_code && <span className="badge" style={{ marginRight: 6 }}>{log.activity_code}</span>}
                                            {log.progress_note}
                                        </td>
                                        <td style={{ padding: 12, textAlign: 'right' }}>{Number(log.hours).toFixed(2)}</td>
                                        <td style={{ padding: 12, textAlign: 'right' }}>${Number(log.amount).toLocaleString('es-CL')}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </Layout>
    );
}
